import { useRef } from "react";
import { buildAllRoomRateplanCombos } from "../utils/roomRateCombos";
import { useOfferTypeDiscount } from "./useOfferTypeDiscount";
import { useOfferTypePropertySelection } from "./useOfferTypePropertySelection";
import { useOfferTypeRoomsRateplans } from "./useOfferTypeRoomsRateplans";

/**
 * Custom hook for managing OfferType component state.
 * Composes three sub-hooks:
 *   1. `useOfferTypePropertySelection` — property list & selected properties
 *   2. `useOfferTypeDiscount`          — offer-type / discount state & handlers
 *   3. `useOfferTypeRoomsRateplans`    — drawer, rooms/rate-plans, combo fetching
 */
export function useOfferTypeState({
  onFieldChange,
  promo,
  setIsNextBtnDisabled,
  calculationType,
  setCalculationType,
  isEdit = false,
  isDuplicate = false,
  isMarket = false,
  ratePlans = [],
  rooms = [],
  // Property that `ratePlans`/`rooms` were fetched for. Used to tag the
  // combos built from them so a selection can be matched back to its owner.
  ratePlansPropertyId = null,
  // CRS of the header-selected property — feeds the calculation-method lock
  // in useOfferTypeDiscount (D-Edge only supports total-price discounts).
  crsId,
  // Flows that hide the "All Rooms & Rate Plans" choice (e.g. campaign
  // approve) should pass true — see useOfferTypeRoomsRateplans.
  forceSelectOnly = false,
}) {
  // Per-property rooms/rate-plans cache. Owned here because two sub-hooks
  // touch it: the rooms/rate-plans hook fills and reads it, and the
  // property-selection hook clears it when the header property changes.
  const roomsRateplansByPropertyRef = useRef({});

  // 1. Property-selection — must come FIRST; its outputs feed the other
  //    sub-hooks.
  const {
    selectedProperty,
    properties,
    selectedProperties,
    setSelectedProperties,
    hotelSelectContainerRef,
    propertyOptions,
    onPropertiesSearch,
    onPropertiesReset,
    filteredPropertyOptionsForHotel,
  } = useOfferTypePropertySelection({
    promo,
    onFieldChange,
    isEdit,
    isDuplicate,
    roomsRateplansByPropertyRef,
  });

  // 2. Discount / offer-type state and handlers (incl. the D-Edge
  //    calculation-method lock derived from the Apply To selection).
  const {
    promoData,
    calculationOptions,
    isCalculationMethodLocked,
    getDiscountValue,
    discountLimitError,
    handlePriceAdjustmentChange,
    handleChange,
    handleDiscountTypeChange,
    handleCalculationTypeChange,
    handleDiscountChange,
    handleDayChange,
    handleKeyDown,
  } = useOfferTypeDiscount({
    promo,
    onFieldChange,
    setIsNextBtnDisabled,
    calculationType,
    setCalculationType,
    crsId,
    selectedProperties,
    propertyOptions,
  });

  // 3. Rooms/rate-plans drawer state and combo logic.
  const {
    showRoomsRateplansModal,
    setShowRoomsRateplansModal,
    drawerSelections,
    setDrawerSelections,
    drawerRatePlans,
    setDrawerRatePlans,
    drawerRooms,
    setDrawerRooms,
    drawerDataPropertyId,
    setDrawerDataPropertyId,
    selectedHotelInDrawer,
    setSelectedHotelInDrawer,
    successMessage,
    setSuccessMessage,
    roomsRateplansValue,
    handleRoomsRateplansValueChange,
    savedSelectionsBeforeDrawerRef,
    roomsRateplansOptions,
    handleDrawerClose,
    handlePropertiesBlur,
    handlePropertiesChange,
  } = useOfferTypeRoomsRateplans({
    promo,
    onFieldChange,
    ratePlans,
    rooms,
    isMarket,
    selectedProperty,
    selectedProperties,
    setSelectedProperties,
    roomsRateplansByPropertyRef,
    ratePlansPropertyId,
    forceSelectOnly,
  });

  // -------------------------------------------------------------------------
  // UI mode helpers
  // -------------------------------------------------------------------------
  const isViewMode = () => isEdit || isDuplicate;
  const getButtonText = () => (isViewMode() ? "View" : "Select");
  const getButtonClassName = () => (isViewMode() ? "view-btn" : "select-btn");

  // -------------------------------------------------------------------------
  // Combined return — preserves the full public surface unchanged
  // -------------------------------------------------------------------------

  return {
    // Context values
    selectedProperty,
    properties,

    // State
    selectedProperties,
    setSelectedProperties,
    promoData,
    calculationOptions,
    isCalculationMethodLocked,
    showRoomsRateplansModal,
    setShowRoomsRateplansModal,
    drawerSelections,
    setDrawerSelections,
    drawerRatePlans,
    setDrawerRatePlans,
    drawerRooms,
    setDrawerRooms,
    drawerDataPropertyId,
    setDrawerDataPropertyId,
    selectedHotelInDrawer,
    setSelectedHotelInDrawer,
    successMessage,
    setSuccessMessage,
    roomsRateplansValue,
    // Deliberately not exporting the raw setter: it bypasses the rebuild that
    // an explicit "all" pick has to trigger.
    handleRoomsRateplansValueChange,

    // Refs
    hotelSelectContainerRef,
    savedSelectionsBeforeDrawerRef,
    roomsRateplansByPropertyRef,

    // Computed values
    roomsRateplansOptions,
    propertyOptions,
    onPropertiesSearch,
    onPropertiesReset,
    filteredPropertyOptionsForHotel,
    getDiscountValue,
    discountLimitError,

    // Handlers
    handlePriceAdjustmentChange,
    handleChange,
    handleDiscountTypeChange,
    handleCalculationTypeChange,
    handleDiscountChange,
    handleDayChange,
    handleKeyDown,
    handleDrawerClose,
    handlePropertiesBlur,
    handlePropertiesChange,
    buildAllRoomRateplanCombos,

    // Helper methods
    isViewMode,
    getButtonText,
    getButtonClassName,
  };
}
